<template>
  <div class="recurrence-loyalty">
    <!-- SUMMARY CARDS - în afara tabelului -->
    <ul class="summary-cards" v-if="summary">
      <li class="summary-card">
        <span class="card-value">{{ totalPatients }}</span>
        <span class="card-label">Total Pacienți</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.recurringPatients }}</span>
        <span class="card-label">Recurenți</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.recurringRate }}</span>
        <span class="card-label">Rată Recurență</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.activePatients }}</span>
        <span class="card-label">Activi</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.dormantPatients }}</span>
        <span class="card-label">Dormanți</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.atRiskPatients }}</span>
        <span class="card-label">La Risc</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.churnedPatients }}</span>
        <span class="card-label">Pierduți</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.active6MonthsCount }}</span>
        <span class="card-label">Activi 6 Luni</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.active12MonthsCount }}</span>
        <span class="card-label">Activi 12 Luni</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.avgLifetimeDays }}</span>
        <span class="card-label">Durată Medie (Zile)</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.avgLifetimeMonths }}</span>
        <span class="card-label">Durată Medie (Luni)</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.avgVisitsPerPatient }}</span>
        <span class="card-label">Medie Vizite / Pacient</span>
      </li>
      <li class="summary-card">
        <span class="card-value">{{ summary.avgDaysBetweenVisits }}</span>
        <span class="card-label">Medie Zile între Vizite</span>
      </li>
    </ul>

    <div class="action-bar">
      <button class="download-btn" @click="$emit('download')" :disabled="isDownloading || isLoading">
        <span v-if="!isDownloading">📥 Descarcă Excel</span>
        <span v-else class="btn-spinner"></span>
      </button>
    </div>

    <!-- COHORT TABLE -->
    <h3 class="section-title">Analiză Cohorte</h3>
    <div class="table-wrapper" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="table-loader-overlay">
        <div class="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>
      <table class="table">
        <thead>
        <tr>
          <th>Cohortă</th>
          <th>Total Pacienți</th>
          <th>Retenție L1</th>
          <th>Retenție L3</th>
          <th>Retenție L6</th>
          <th>Retenție L12</th>
          <th>Activi Acum</th>
          <th>Pierduți Acum</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="!cohortAnalysis.length">
          <td colspan="8" class="no-data">Nu există date disponibile</td>
        </tr>
        <tr v-else v-for="cohort in cohortAnalysis" :key="cohort.cohort">
          <td>{{ cohort.cohort }}</td>
          <td>{{ cohort.totalPatients }}</td>
          <td>{{ cohort.retention.month1 !== 'N/A' ? cohort.retention.month1.rate : '-' }}</td>
          <td>{{ cohort.retention.month3 !== 'N/A' ? cohort.retention.month3.rate : '-' }}</td>
          <td>{{ cohort.retention.month6 !== 'N/A' ? cohort.retention.month6.rate : '-' }}</td>
          <td>{{ cohort.retention.month12 !== 'N/A' ? cohort.retention.month12.rate : '-' }}</td>
          <td>{{ cohort.currentlyActive }}</td>
          <td>{{ cohort.currentlyChurned }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- PATIENTS TABLE -->
    <h3 class="section-title">Pacienți</h3>
    <div class="table-wrapper" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="table-loader-overlay">
        <div class="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>
      <table class="table">
        <thead>
        <tr>
          <th>#</th>
          <th>Nume</th>
          <th>Prenume</th>
          <th>Oraș</th>
          <th>Telefon</th>
          <th>Email</th>
          <th>Prima Vizită</th>
          <th>Ultima Vizită</th>
          <th>Total Vizite</th>
          <th>Durată (Zile)</th>
          <th>Durată (Luni)</th>
          <th>Zile de la Ultima Vizită</th>
          <th>Medie Zile între Vizite</th>
          <th>Cohortă</th>
          <th>Status</th>
          <th>Activ 6 Luni</th>
          <th>Activ 12 Luni</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="patients.length === 0">
          <td colspan="17" class="no-data">Nu există date disponibile</td>
        </tr>
        <tr v-else v-for="(item, index) in patients" :key="item._id">
          <td>{{ index + 1 }}</td>
          <td>{{ item.nume }}</td>
          <td>{{ item.prenume }}</td>
          <td>{{ item.oras }}</td>
          <td>{{ item.telefon || '-' }}</td>
          <td>{{ item.email !== 'N/A' ? item.email : '-' }}</td>
          <td>{{ item.firstVisit }}</td>
          <td>{{ item.lastVisit }}</td>
          <td>{{ item.totalVisits }}</td>
          <td>{{ item.lifetimeDays }}</td>
          <td>{{ item.lifetimeMonths }}</td>
          <td>{{ item.daysSinceLastVisit }}</td>
          <td>{{ item.avgDaysBetweenVisits }}</td>
          <td>{{ item.cohort }}</td>
          <td>{{ item.status }}</td>
          <td>{{ item.active6Months ? 'Da' : 'Nu' }}</td>
          <td>{{ item.active12Months ? 'Da' : 'Nu' }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecurrenceLoyalty',

  props: {
    totalPatients: { type: Number, default: 0 },
    summary: { type: Object, default: () => null },
    cohortAnalysis: { type: Array, default: () => [] },
    patients: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    isDownloading: { type: Boolean, default: false }
  },

  emits: ['download']
}
</script>

<style lang="less">
@import '../assets/styles/var.less';

.recurrence-loyalty {

  .section-title {
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8e8e93;
    margin: 28px 0 10px;
  }

  .summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
    margin-bottom: 28px;

    .summary-card {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 12px;
      padding: 14px 16px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      .card-value {
        font-size: 22px;
        font-weight: 700;
        color: #1c1c1e;
        letter-spacing: -0.02em;
        line-height: 1.2;
        margin-bottom: 4px;
      }

      .card-label {
        font-size: 13px;
        font-weight: 500;
        color: #8e8e93;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
    }
  }
}
</style>
